// src/pages/products/CreateProduct/hooks/useFormData.js
import { useState, useEffect, useCallback } from 'react';
import { getProductAttributes } from '../../../../services/api/attributes';
import { getShops } from '../../../../services/api/shops';
import { getWarehouses } from '../../../../services/api/warehouses';
import { checkProductSku } from '../../../../services/api/products';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { validateBundle } from '../../../../components/product/BundleEditor/bundleUtils';
import { hasPrice } from '../../../../utils/priceUtils';

const useFormData = () => {
  const { t } = useLocalization();
  
  const [formData, setFormData] = useState({
    name: '',
    printableName: '',
    description: '',
    productType: 'simple',
    visibility: 'public',
    buyingPrice: '',
    sku: '',
    barcode: '',
    weight: '',
    brandId: '',
    categoryId: null,
    tagIds: [],
    attributeIds: [],
    attributeValueIds: [],
    shopData: [],
    openingStock: [],
    variations: [],
    bundleItems: [],
    bundleShopIds: [],
    images: [null, null, null, null, null], // 5 slots total (main + 4 gallery)
    mainImageIndex: 0
  });
  
  const [attributes, setAttributes] = useState([]);
  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');

  // SKU validation state
  const [skuValidation, setSkuValidation] = useState({
    mainSku: { isValidating: false, isValid: true },
    variations: {}
  });

  // Variations state
  const [variationCombinations, setVariationCombinations] = useState([]);
  const [expandedVariations, setExpandedVariations] = useState(new Set());
  const [selectedVariations, setSelectedVariations] = useState(new Set());
  
  // Bulk actions state
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkShopAssignments, setBulkShopAssignments] = useState({});
  const [bulkPrices, setBulkPrices] = useState({});
  const [bulkCostPrice, setBulkCostPrice] = useState('');
  const [bulkWeight, setBulkWeight] = useState('');

  // Debounced SKU validation function
  const validateSku = useCallback(
    debounce(async (sku, type, variationIndex = null) => {
      if (!sku.trim()) {
        // Clear validation state if SKU is empty
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: false, isValid: true }
          }));
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: false, isValid: true }
            }
          }));
        }
        return;
      }

      try {
        // Set validating state
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: true, isValid: true }
          }));
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: true, isValid: true }
            }
          }));
        }

        const response = await checkProductSku(sku);
        const isValid = response.valid;

        // Update validation state
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: false, isValid }
          }));

          // Update errors
          if (!isValid) {
            setErrors(prev => ({
              ...prev,
              sku: 'This SKU is already in use'
            }));
          } else {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.sku;
              return newErrors;
            });
          }
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: false, isValid }
            }
          }));

          // Update variation errors
          if (!isValid) {
            setErrors(prev => ({
              ...prev,
              variations: {
                ...prev.variations,
                [variationIndex]: {
                  ...prev.variations?.[variationIndex],
                  sku: 'This SKU is already in use'
                }
              }
            }));
          } else {
            setErrors(prev => {
              const newErrors = { ...prev };
              if (newErrors.variations?.[variationIndex]?.sku) {
                delete newErrors.variations[variationIndex].sku;
                if (Object.keys(newErrors.variations[variationIndex]).length === 0) {
                  delete newErrors.variations[variationIndex];
                }
                if (Object.keys(newErrors.variations).length === 0) {
                  delete newErrors.variations;
                }
              }
              return newErrors;
            });
          }
        }
      } catch (error) {
        console.error('SKU validation error:', error);
        // Reset validating state on error
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: false, isValid: true }
          }));
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: false, isValid: true }
            }
          }));
        }
      }
    }, 500),
    []
  );

  // Available tabs based on product type
  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'basic', label: t('products.basicInfo'), icon: 'Info' },
      { id: 'pricing', label: t('products.pricing'), icon: 'DollarSign' },
      { id: 'attributes', label: t('products.attributes'), icon: 'Tag' },
    ];

    if (formData.productType === 'variable') {
      baseTabs.push({ id: 'variations', label: 'Variations', icon: 'Settings' });
    }

    baseTabs.push({ id: 'images', label: t('products.images'), icon: 'ImageIcon' });
    return baseTabs;
  };

  // Fetch required data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        
        const [
          attributesResponse, 
          shopsResponse,
          warehousesResponse
        ] = await Promise.all([
          getProductAttributes(),
          getShops(),
          getWarehouses()
        ]);
        
        setAttributes(attributesResponse.attributes || []);
        setShops(shopsResponse || []);
        setWarehouses(warehousesResponse || []);
        
        // Initialize shop data for all shops
        if (shopsResponse && shopsResponse.length > 0) {
          const initialShopData = shopsResponse.map(shop => ({
            shopId: shop.id,
            salesPrice: '',
            dropshippingPrice: '',
            enabled: false,
            overrideDeliveryChargeEnabled: false,
            overrideDeliveryCharge: null,
            offers: [],
          }));
          setFormData(prev => ({
            ...prev,
            shopData: initialShopData
          }));
        }

        // Initialize opening stock for all warehouses
        if (warehousesResponse && warehousesResponse.length > 0) {
          const initialOpeningStock = warehousesResponse.map(warehouse => ({
            warehouseId: warehouse.id,
            stock: ''
          }));
          setFormData(prev => ({
            ...prev,
            openingStock: initialOpeningStock
          }));
        }
        
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setErrors({ fetch: t('products.failedToLoadData') });
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [t]);

  // Generate variations when attributes change
  useEffect(() => {
    if (formData.productType === 'variable' && formData.attributeIds.length > 0) {
      generateVariations();
    }
  }, [formData.attributeIds, formData.attributeValueIds, formData.productType, attributes]);

  // Reset activeTab when product type changes
  useEffect(() => {
    if (formData.productType !== 'variable' && activeTab === 'variations') {
      setActiveTab('basic');
    }
    if (formData.productType !== 'bundle' && activeTab === 'bundle') {
      setActiveTab('basic');
    }
    if (formData.productType === 'bundle' && ['pricing', 'attributes', 'dropshipping'].includes(activeTab)) {
      setActiveTab('basic');
    }
  }, [formData.productType, activeTab]);

  const generateVariations = () => {
    // Get attributes that are used for variations
    const variationAttributeIds = formData.attributeIds
      .filter(attr => attr.usedForVariations)
      .map(attr => attr.attributeId);

    if (variationAttributeIds.length === 0) {
      setVariationCombinations([]);
      setFormData(prev => ({ ...prev, variations: [] }));
      setExpandedVariations(new Set());
      setSelectedVariations(new Set());
      return;
    }

    const attributeValues = variationAttributeIds.map(attrId => {
      const attribute = attributes.find(attr => attr.id === attrId);
      return attribute ? attribute.values.filter(val => 
        formData.attributeValueIds.includes(val.id)
      ) : [];
    });

    if (attributeValues.some(values => values.length === 0)) {
      setVariationCombinations([]);
      setFormData(prev => ({ ...prev, variations: [] }));
      setExpandedVariations(new Set());
      setSelectedVariations(new Set());
      return;
    }

    // Generate all combinations
    const combinations = cartesianProduct(attributeValues);
    const variations = combinations.map((combo, index) => ({
      id: Date.now() + index,
      sku: '',
      costPrice: '',
      barcode: '',
      weight: '',
      attributeValueIds: combo.map(value => value.id),
      attributeValues: combo,
      variationImage: null,
      dropshippingEnabled: false,
      dropshippingPrice: '',
      minimumSellingPrice: '',
      maximumSellingPrice: '',
      shopData: shops.map(shop => ({
        shopId: shop.id,
        salesPrice: '',
        dropshippingPrice: '',
        enabled: false,
        overrideDeliveryChargeEnabled: false,
        overrideDeliveryCharge: null,
        offers: [],
      })),
      openingStock: warehouses.map(warehouse => ({
        warehouseId: warehouse.id,
        stock: ''
      }))
    }));

    setVariationCombinations(variations);
    setFormData(prev => ({
      ...prev,
      variations: variations
    }));
    
    // Reset expanded and selected states
    setExpandedVariations(new Set());
    setSelectedVariations(new Set());
    // Reset SKU validation for variations
    setSkuValidation(prev => ({
      ...prev,
      variations: {}
    }));
  };

  const cartesianProduct = (arrays) => {
    return arrays.reduce((acc, curr) => 
      acc.flatMap(x => curr.map(y => [...x, y])), [[]]
    );
  };

  // Variation management functions
  const toggleVariationExpansion = (variationId) => {
    setExpandedVariations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const toggleVariationSelection = (variationId) => {
    setSelectedVariations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const selectAllVariations = () => {
    const allIds = formData.variations.map(v => v.id);
    setSelectedVariations(new Set(allIds));
  };

  const deselectAllVariations = () => {
    setSelectedVariations(new Set());
  };

  const removeSelectedVariations = () => {
    const filteredVariations = formData.variations.filter(v => !selectedVariations.has(v.id));
    setFormData(prev => ({
      ...prev,
      variations: filteredVariations
    }));
    setSelectedVariations(new Set());
    setExpandedVariations(prev => {
      const newSet = new Set(prev);
      selectedVariations.forEach(id => newSet.delete(id));
      return newSet;
    });
  };

  const regenerateVariations = () => {
    generateVariations();
  };

  // Bulk actions functions
  const handleBulkShopAssignment = (shopId, enabled) => {
    setBulkShopAssignments(prev => ({
      ...prev,
      [shopId]: enabled
    }));
  };

  const handleBulkPriceChange = (shopId, price) => {
    setBulkPrices(prev => ({
      ...prev,
      [shopId]: price
    }));
  };

  const applyBulkActions = (successCallback, errorCallback) => {
    if (selectedVariations.size === 0) {
      if (errorCallback) errorCallback('Please select variations to apply bulk actions');
      return;
    }

    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map(variation => {
        if (selectedVariations.has(variation.id)) {
          return {
            ...variation,
            costPrice: bulkCostPrice || variation.costPrice,
            weight: bulkWeight || variation.weight,
            shopData: variation.shopData.map(shop => ({
              ...shop,
              enabled: bulkShopAssignments[shop.shopId] !== undefined ? 
                bulkShopAssignments[shop.shopId] : shop.enabled,
              salesPrice: bulkPrices[shop.shopId] !== undefined ? 
                bulkPrices[shop.shopId] : shop.salesPrice,
              dropshippingPrice: bulkPrices[`${shop.shopId}_dropshipping`] !== undefined ? 
                bulkPrices[`${shop.shopId}_dropshipping`] : shop.dropshippingPrice
            }))
          };
        }
        return variation;
      })
    }));

    // Reset bulk actions
    setBulkCostPrice('');
    setBulkWeight('');
    setBulkShopAssignments({});
    setBulkPrices({});
    setShowBulkActions(false);
    setSelectedVariations(new Set());
    
    if (successCallback) successCallback('Bulk actions applied successfully');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Validate SKU if it's the SKU field
    if (name === 'sku') {
      validateSku(value, 'main');
    }
    
    // Clear error when user starts typing (except for SKU which is handled by validation)
    if (errors[name] && name !== 'sku') {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBundleChange = ({ bundleItems, bundleShopIds }) => {
    setFormData(prev => ({ ...prev, bundleItems, bundleShopIds }));
    if (errors.bundleItems || errors.bundleShops) {
      setErrors(prev => ({ ...prev, bundleItems: '', bundleShops: '' }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData(prev => ({
      ...prev,
      description: content
    }));
    
    if (errors.description) {
      setErrors(prev => ({
        ...prev,
        description: ''
      }));
    }
  };

  const handleAttributesChange = (newAttributeIds) => {
    setFormData(prev => ({
      ...prev,
      attributeIds: newAttributeIds
    }));

    if (errors.attributes) {
      setErrors(prev => ({
        ...prev,
        attributes: ''
      }));
    }
  };

  const handleAttributeValuesChange = (newAttributeValueIds) => {
    setFormData(prev => ({
      ...prev,
      attributeValueIds: newAttributeValueIds
    }));
  };

  const handleCategorySelect = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categoryId: categoryId
    }));

    if (errors.categoryId) {
      setErrors(prev => ({
        ...prev,
        categoryId: ''
      }));
    }
  };

  const handleTagsChange = (newTagIds) => {
    setFormData(prev => ({
      ...prev,
      tagIds: newTagIds
    }));
    
    if (errors.tagIds) {
      setErrors(prev => ({
        ...prev,
        tagIds: ''
      }));
    }
  };

  const handleBrandChange = (brandId) => {
    setFormData(prev => ({
      ...prev,
      brandId: brandId || ''
    }));
    
    if (errors.brandId) {
      setErrors(prev => ({
        ...prev,
        brandId: ''
      }));
    }
  };

  const handleShopDataChange = (shopIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      shopData: prev.shopData.map((shop, index) => 
        index === shopIndex ? { ...shop, [field]: value } : shop
      )
    }));

    if (errors.shopData && errors.shopData[shopIndex] && errors.shopData[shopIndex][field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.shopData && newErrors.shopData[shopIndex]) {
          delete newErrors.shopData[shopIndex][field];
          if (Object.keys(newErrors.shopData[shopIndex]).length === 0) {
            delete newErrors.shopData[shopIndex];
          }
        }
        return newErrors;
      });
    }
  };

  const handleOpeningStockChange = (warehouseIndex, value) => {
    setFormData(prev => ({
      ...prev,
      openingStock: prev.openingStock.map((stock, index) => 
        index === warehouseIndex ? { ...stock, stock: value } : stock
      )
    }));

    if (errors.openingStock && errors.openingStock[warehouseIndex]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.openingStock && newErrors.openingStock[warehouseIndex]) {
          delete newErrors.openingStock[warehouseIndex];
        }
        return newErrors;
      });
    }
  };

  const handleVariationChange = (variationIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, index) => 
        index === variationIndex ? { ...variation, [field]: value } : variation
      )
    }));

    // Validate SKU if it's the SKU field
    if (field === 'sku') {
      validateSku(value, 'variation', variationIndex);
    }

    // Clear error when user starts typing (except for SKU which is handled by validation)
    if (errors.variations && errors.variations[variationIndex] && errors.variations[variationIndex][field] && field !== 'sku') {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.variations && newErrors.variations[variationIndex]) {
          delete newErrors.variations[variationIndex][field];
          if (Object.keys(newErrors.variations[variationIndex]).length === 0) {
            delete newErrors.variations[variationIndex];
          }
        }
        return newErrors;
      });
    }
  };

  const handleVariationShopDataChange = (variationIndex, shopIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, vIndex) => 
        vIndex === variationIndex ? {
          ...variation,
          shopData: variation.shopData.map((shop, sIndex) => 
            sIndex === shopIndex ? { ...shop, [field]: value } : shop
          )
        } : variation
      )
    }));

    // Clear errors
    if (errors.variations && errors.variations[variationIndex]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.variations[variationIndex]?.shopDataDetails?.[shopIndex]?.[field]) {
          delete newErrors.variations[variationIndex].shopDataDetails[shopIndex][field];
        }
        return newErrors;
      });
    }
  };

  const handleVariationStockChange = (variationIndex, warehouseIndex, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, vIndex) => 
        vIndex === variationIndex ? {
          ...variation,
          openingStock: variation.openingStock.map((stock, wIndex) => 
            wIndex === warehouseIndex ? { ...stock, stock: value } : stock
          )
        } : variation
      )
    }));

    // Clear errors
    if (errors.variations && errors.variations[variationIndex]?.openingStock?.[warehouseIndex]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.variations[variationIndex]?.openingStock?.[warehouseIndex]) {
          delete newErrors.variations[variationIndex].openingStock[warehouseIndex];
        }
        return newErrors;
      });
    }
  };

  const handleVariationImageChange = (variationIndex, file) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, index) => 
        index === variationIndex ? { ...variation, variationImage: file } : variation
      )
    }));
  };

  const handleImageUpload = (index, file) => {
    if (file) {
      const newImages = [...formData.images];
      newImages[index] = file;
      setFormData(prev => ({
        ...prev,
        images: newImages
      }));
    }
  };

  const handleImageRemove = (index) => {
    const newImages = [...formData.images];
    newImages[index] = null;
    
    // If removing the main image, set the first available image as main
    let newMainIndex = formData.mainImageIndex;
    if (index === formData.mainImageIndex) {
      newMainIndex = newImages.findIndex((img, i) => img !== null && i !== index);
      if (newMainIndex === -1) newMainIndex = 0;
    }
    
    setFormData(prev => ({
      ...prev,
      images: newImages,
      mainImageIndex: newMainIndex
    }));
  };

  const handleSetMainImage = (index) => {
    if (formData.images[index]) {
      setFormData(prev => ({
        ...prev,
        mainImageIndex: index
      }));
    }
  };

  const handleVariationDropshippingChange = (variationIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, idx) =>
        idx === variationIndex ? { ...variation, [field]: value } : variation
      )
    }));
  };

  const applyBulkDropshipping = (bulkData) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map(variation => ({
        ...variation,
        dropshippingEnabled: bulkData.dropshippingEnabled,
        ...(bulkData.dropshippingPrice !== '' && { dropshippingPrice: bulkData.dropshippingPrice }),
        ...(bulkData.minimumSellingPrice !== '' && { minimumSellingPrice: bulkData.minimumSellingPrice }),
        ...(bulkData.maximumSellingPrice !== '' && { maximumSellingPrice: bulkData.maximumSellingPrice }),
      }))
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = t('products.nameRequired');
    }

    if (!formData.productType) {
      newErrors.productType = t('products.productTypeRequired');
    }

    if (!formData.visibility) {
      newErrors.visibility = t('products.visibilityRequired');
    }

    // Category validation (now required)
    if (!formData.categoryId) {
      newErrors.categoryId = t('products.categoryRequired') || 'Please select a category';
    }

    // SKU validation - check if any SKU is currently being validated or invalid
    if (formData.sku.trim() && (!skuValidation.mainSku.isValid || skuValidation.mainSku.isValidating)) {
      if (skuValidation.mainSku.isValidating) {
        newErrors.sku = 'SKU validation in progress...';
      } else if (!skuValidation.mainSku.isValid) {
        newErrors.sku = 'This SKU is already in use';
      }
    }

    if (formData.productType === 'bundle') {
      Object.assign(newErrors, validateBundle(formData.bundleItems, formData.bundleShopIds, t));
    } else if (formData.productType === 'variable') {
      const variationAttributes = formData.attributeIds.filter(attr => attr.usedForVariations);
      if (variationAttributes.length === 0) {
        newErrors.attributes = t('products.attributesRequiredForVariable');
      }

      // Check if at least one variation remains
      if (formData.variations.length === 0) {
        newErrors.variations = 'At least one variation is required';
      }

      // Validate each variation
      const variationErrors = [];
      formData.variations.forEach((variation, index) => {
        const varErrors = {};

        // SKU validation for variations
        if (variation.sku.trim()) {
          const variationSkuValidation = skuValidation.variations[index];
          if (variationSkuValidation && (!variationSkuValidation.isValid || variationSkuValidation.isValidating)) {
            if (variationSkuValidation.isValidating) {
              varErrors.sku = 'SKU validation in progress...';
            } else if (!variationSkuValidation.isValid) {
              varErrors.sku = 'This SKU is already in use';
            }
          }
        }

        // Check if any shop has sales price
        const hasValidShopData = variation.shopData.some(shop => shop.enabled && hasPrice(shop.salesPrice));
        if (!hasValidShopData) {
          varErrors.shopData = 'At least one shop must be enabled with a sales price';
        }

        // Validate individual shop data
        const shopDataErrors = [];
        variation.shopData.forEach((shopData, shopIndex) => {
          if (shopData.enabled && !hasPrice(shopData.salesPrice)) {
            shopDataErrors[shopIndex] = { salesPrice: 'Sales price is required for enabled shops' };
          }
        });
        if (shopDataErrors.length > 0) {
          varErrors.shopDataDetails = shopDataErrors;
        }

        // Validate opening stock
        const stockErrors = [];
        variation.openingStock.forEach((stock, stockIndex) => {
          if (stock.stock !== '' && stock.stock < 0) {
            stockErrors[stockIndex] = { stock: 'Stock must be 0 or greater' };
          }
        });
        if (stockErrors.length > 0) {
          varErrors.openingStock = stockErrors;
        }

        // Validate dropshipping fields when enabled
        if (variation.dropshippingEnabled) {
          if (!variation.dropshippingPrice) {
            varErrors.dropshippingPrice = t('dropshipping.dropshippingPriceRequired') || 'Dropshipping price is required';
          }
          if (!variation.minimumSellingPrice) {
            varErrors.minimumSellingPrice = t('dropshipping.minPriceRequired') || 'Minimum selling price is required';
          }
          if (!variation.maximumSellingPrice) {
            varErrors.maximumSellingPrice = t('dropshipping.maxPriceRequired') || 'Maximum selling price is required';
          }
        }

        if (Object.keys(varErrors).length > 0) {
          variationErrors[index] = varErrors;
        }
      });

      if (variationErrors.length > 0) {
        newErrors.variations = variationErrors;
      }
    } else {
      // For simple products, validate shop data and opening stock
      const shopDataErrors = [];
      formData.shopData.forEach((shop, index) => {
        if (shop.enabled) {
          const shopErrors = {};
          if (!hasPrice(shop.salesPrice)) {
            shopErrors.salesPrice = t('products.salesPriceRequired');
          }
          if (shop.type === 'dropshipper' && (!shop.dropshippingPrice || shop.dropshippingPrice <= 0)) {
            shopErrors.dropshippingPrice = t('products.dropshippingPriceRequired');
          }
          if (Object.keys(shopErrors).length > 0) {
            shopDataErrors[index] = shopErrors;
          }
        }
      });

      if (shopDataErrors.length > 0) {
        newErrors.shopData = shopDataErrors;
      }

      // Opening stock validation
      const openingStockErrors = [];
      formData.openingStock.forEach((stock, index) => {
        const stockErrors = {};
        if (stock.stock !== '' && stock.stock < 0) {
          stockErrors.stock = 'Opening stock must be 0 or greater';
        }
        if (Object.keys(stockErrors).length > 0) {
          openingStockErrors[index] = stockErrors;
        }
      });

      if (openingStockErrors.length > 0) {
        newErrors.openingStock = openingStockErrors;
      }
    }

    // Image validation
    if (formData.mainImage) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(formData.mainImage.type)) {
        newErrors.mainImage = t('products.invalidImageFormat');
      } else if (formData.mainImage.size > 5 * 1024 * 1024) {
        newErrors.mainImage = t('products.imageTooLarge');
      }
    }

    const validImages = formData.images.filter(img => img !== null);
    if (validImages.length > 0) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      const invalidImages = validImages.some(img => 
        !allowedTypes.includes(img.type) || img.size > 5 * 1024 * 1024
      );
      if (invalidImages) {
        newErrors.images = t('products.invalidGalleryImages');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    // State
    formData,
    attributes,
    shops,
    warehouses,
    errors,
    isLoadingData,
    activeTab,
    variationCombinations,
    expandedVariations,
    selectedVariations,
    showBulkActions,
    bulkShopAssignments,
    bulkPrices,
    bulkCostPrice,
    bulkWeight,
    skuValidation, // Add SKU validation state to return

    // Setters
    setFormData,
    setErrors,
    setActiveTab,
    setShowBulkActions,
    setBulkCostPrice,
    setBulkWeight,

    // Utility functions
    getAvailableTabs,
    validateForm,

    // Handler functions
    handleInputChange,
    handleDescriptionChange,
    handleBundleChange,
    handleAttributesChange,
    handleAttributeValuesChange,
    handleCategorySelect,
    handleTagsChange,
    handleBrandChange,
    handleShopDataChange,
    handleOpeningStockChange,
    handleVariationChange,
    handleVariationShopDataChange,
    handleVariationStockChange,
    handleVariationImageChange,
    handleImageUpload,
    handleImageRemove,
    handleSetMainImage,

    // Variation management
    toggleVariationExpansion,
    toggleVariationSelection,
    selectAllVariations,
    deselectAllVariations,
    removeSelectedVariations,
    regenerateVariations,

    // Bulk actions
    handleBulkShopAssignment,
    handleBulkPriceChange,
    applyBulkActions,

    // Dropshipping variation handlers
    handleVariationDropshippingChange,
    applyBulkDropshipping,
  };
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default useFormData;